// Wunder City Design System Theme Configuration
// Based on the Pensacola coastal identity and community spirit

export const colors = {
  // Primary Colors
  primary: {
    oceanBlue: '#0EA5E9',
    white: '#FFFFFF',
    navy: '#0F172A'
  },
  
  // Secondary Colors
  secondary: {
    sunsetCoral: '#FB7185',
    sandBeige: '#FEF3C7',
    seafoamGreen: '#6EE7B7'
  },
  
  // Accent Colors
  accent: {
    pelicanOrange: '#FB923C',
    historicBrick: '#DC2626',
    twilightPurple: '#A78BFA',
    marinaGray: '#64748B'
  },
  
  // Functional Colors
  functional: {
    successGreen: '#10B981',
    errorRed: '#EF4444',
    warningAmber: '#F59E0B',
    infoBlue: '#3B82F6'
  },
  
  // Background Colors
  background: {
    primary: '#FFFFFF',
    secondary: '#F8FAFC',
    tertiary: '#F1F5F9',
    darkPrimary: '#0F172A',
    darkSecondary: '#1E293B'
  },
  
  // Gray Scale
  gray: {
    50: '#F9FAFB',
    100: '#F3F4F6',
    200: '#E5E7EB',
    300: '#D1D5DB',
    400: '#9CA3AF',
    500: '#6B7280',
    600: '#4B5563',
    700: '#374151',
    800: '#1F2937',
    900: '#111827'
  }
};

export const typography = {
  // Font Families
  fontFamily: {
    primary: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    display: 'Outfit, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    mono: 'JetBrains Mono, Menlo, Monaco, Consolas, "Courier New", monospace'
  },
  
  // Font Weights
  fontWeight: {
    light: 300,
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700
  },
  
  // Font Sizes
  fontSize: {
    xs: '0.75rem',    // 12px
    sm: '0.875rem',   // 14px
    base: '1rem',     // 16px
    lg: '1.125rem',   // 18px
    xl: '1.25rem',    // 20px
    '2xl': '1.5rem',  // 24px
    '3xl': '1.875rem', // 30px
    '4xl': '2.25rem', // 36px
    '5xl': '3rem'     // 48px
  }
};

export const spacing = {
  0: '0',
  1: '0.25rem',   // 4px
  2: '0.5rem',    // 8px
  3: '0.75rem',   // 12px
  4: '1rem',      // 16px
  5: '1.25rem',   // 20px
  6: '1.5rem',    // 24px
  8: '2rem',      // 32px
  10: '2.5rem',   // 40px
  12: '3rem',     // 48px
  16: '4rem',     // 64px
  20: '5rem',     // 80px
};

export const borderRadius = {
  none: '0',
  sm: '0.125rem',    // 2px
  base: '0.25rem',   // 4px
  md: '0.375rem',    // 6px
  lg: '0.5rem',      // 8px
  xl: '0.75rem',     // 12px
  '2xl': '1rem',     // 16px
  '3xl': '1.5rem',   // 24px
  full: '9999px'
};

export const shadows = {
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
  none: 'none'
};

export const animation = {
  // Transitions
  transition: {
    fast: '150ms',
    base: '200ms',
    slow: '300ms',
    slower: '500ms'
  },
  
  // Easing
  easing: {
    ease: 'ease',
    easeIn: 'ease-in',
    easeOut: 'ease-out',
    easeInOut: 'ease-in-out'
  }
};

// Component-specific styles
export const components = {
  // Button styles
  button: {
    primary: `bg-sky-500 text-white hover:bg-sky-600 transition-all transform hover:scale-[0.98]`,
    secondary: `bg-white text-gray-900 border-2 border-gray-100 hover:border-sky-200 transition-all transform hover:scale-[0.98]`,
    height: {
      sm: 'h-10',
      md: 'h-12',
      lg: 'h-14'
    },
    padding: {
      sm: 'px-4 py-2',
      md: 'px-6 py-3',
      lg: 'px-6 py-4'
    },
    rounded: 'rounded-2xl',
    font: 'font-semibold'
  },
  
  // Card styles
  card: {
    base: 'bg-white rounded-2xl shadow-sm',
    padding: 'p-4',
    hover: 'hover:shadow-md transition-shadow'
  },
  
  // Input styles
  input: {
    base: 'bg-gray-100 rounded-2xl px-4 py-3 text-gray-900 placeholder-gray-500 border-2 border-transparent focus:border-sky-500 focus:bg-white transition-all',
    height: 'h-14'
  },
  
  // Tab bar styles
  tabBar: {
    container: 'absolute bottom-0 left-0 right-0 bg-white border-t border-gray-100',
    button: 'flex flex-col items-center gap-1 py-2 px-4',
    iconActive: 'text-sky-600',
    iconInactive: 'text-gray-400',
    labelActive: 'text-xs text-sky-600',
    labelInactive: 'text-xs text-gray-600'
  },
  
  // Status bar styles (iPhone mockup)
  statusBar: {
    container: 'absolute top-0 left-0 right-0 h-11 bg-white z-50 flex items-center justify-between px-8',
    time: 'text-xs font-medium',
    battery: 'flex gap-1'
  },
  
  // Phone mockup styles
  phoneMockup: {
    outer: 'w-[375px] h-[812px] bg-black rounded-[3rem] p-4 shadow-2xl',
    inner: 'w-full h-full bg-white rounded-[2.5rem] overflow-hidden relative',
    scale: {
      mobile: 'scale-75',
      tablet: 'md:scale-90',
      desktop: 'lg:scale-100'
    }
  },
  
  // Label styles for phone mockups
  mockupLabel: {
    className: 'text-lg font-semibold text-gray-700',
    spacing: {
      top: 'mt-2 md:mt-3 lg:mt-4',
      bottom: 'mb-8 md:mb-10 lg:mb-6'
    }
  }
};

// Gradient presets
export const gradients = {
  oceanWave: 'bg-gradient-to-b from-sky-50 to-white',
  skyBlue: 'bg-gradient-to-br from-sky-400 to-sky-600',
  skyBlueHorizontal: 'bg-gradient-to-r from-sky-500 to-sky-600',
  sunset: 'bg-gradient-to-r from-pink-500 to-orange-500',
  coral: 'bg-gradient-to-br from-pink-400 to-orange-400',
  primary: 'bg-gradient-to-br from-sky-500 to-sky-600'
};

// Responsive breakpoints
export const breakpoints = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1536px'
};

// Z-index scale
export const zIndex = {
  0: 0,
  10: 10,
  20: 20,
  30: 30,
  40: 40,
  50: 50,
  auto: 'auto'
};

// Export complete theme object
const theme = {
  colors,
  typography,
  spacing,
  borderRadius,
  shadows,
  animation,
  components,
  gradients,
  breakpoints,
  zIndex
};

export default theme;